import type {
  IFdFarmAssetType,
  IFdFinancialPerformance,
  IFdLiability,
} from 'constants/interfaces';

import type {
  AssetDraft,
  LiabilityDraft,
  FinancialHistoryDraft,
} from './financials.types';

export function lshToNumber(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

export function lshToText(value: unknown, fallback = 'Not provided'): string {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

export function lshFormatCompactCurrency(value: unknown): string {
  const amount = lshToNumber(value);
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function lshParseCurrency(
  value: string | number | null | undefined,
): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const cleaned = String(value).replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function lshNormalizeCurrency(
  value: string | number | null | undefined,
): string {
  return lshParseCurrency(value).toFixed(2);
}

export function lshFormatStoredValue(
  value: string | number | undefined | null,
) {
  if (value === null || value === undefined) return '';
  const amount = lshParseCurrency(value);
  return amount === 0 ? '' : amount.toFixed(2);
}

export function lshSanitizeCurrencyInput(value: string): string {
  const cleaned = value
    .replace(/[^\d.,-]/g, '')
    .replace(/,/g, '.')
    .replace(/^(-)?\./, '$10.');

  const parts = cleaned.split('.');
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
}

export function lshBuildFinancialYearOptions() {
  const currentYear = new Date().getFullYear();
  return Array.from({length: 6}, (_, index) => {
    const year = currentYear - index;
    return {
      value: String(year),
      label: String(year),
    };
  });
}

export function lshCreateFinancialSummaryDrafts(
  records: IFdFinancialPerformance[] | undefined,
): FinancialHistoryDraft[] {
  const currentYear = new Date().getFullYear();

  return Array.from({length: 3}, (_, index) => {
    const year = currentYear - (index + 1);
    const record = (records || []).find(
      (item) => lshToNumber(item.year) === year,
    );
    const revenue = lshToNumber(record?.revenue);
    const expenses = lshToNumber(record?.expenses);
    const operatingCosts = lshToNumber(record?.operatingCosts);

    return {
      id: record?.id,
      year,
      revenue: lshNormalizeCurrency(revenue),
      expenses: lshNormalizeCurrency(expenses),
      operatingCosts: lshNormalizeCurrency(operatingCosts),
      profitLoss: lshNormalizeCurrency(revenue - expenses - operatingCosts),
      notes: String(record?.notes || ''),
      financialYearEnd: record?.financialYearEnd
        ? String(record.financialYearEnd)
        : undefined,
      financialYearEndMonth: record?.financialYearEndMonth
        ? String(record.financialYearEndMonth)
        : undefined,
    };
  });
}

export function lshCreateLocalId() {
  return Number(`${Date.now()}${Math.floor(Math.random() * 1000)}`);
}

export function lshMapAssetToDraft(
  asset: IFdFarmAssetType,
  index: number,
): AssetDraft {
  return {
    localId: Number(asset.id) || lshCreateLocalId() + index,
    backendId: Number(asset.id) || undefined,
    assetListId: String(asset.assetListItem?.id || ''),
    assetName: asset.assetListItem?.name || asset.description || 'Asset',
    assetCategory: asset.assetListItem?.assetCategory,
    assetUnit: asset.assetListItem?.assetUnit,
    assetType: asset.assetType,
    description: asset.description || '',
    year: asset.year || '',
    condition: asset.condition || '',
    make: asset.make || '',
    model: asset.model || '',
    capacity: asset.capacity || '',
    value: lshFormatStoredValue(asset.value),
    pricePerUnit: asset.pricePerUnit
      ? lshFormatStoredValue(asset.pricePerUnit)
      : '',
  };
}

export {
  lshGetOperatingCostTotalFromMonthlyCosts,
  lshParseOperatingCostMonthlyCosts,
  lshSerializeOperatingCostMonthlyCosts,
} from '../../../ApplicationTabs/FinancialInformationTab/utils/operatingCostMonthlyCosts';

export function lshMapLiabilityToDraft(
  liability: IFdLiability,
  index = 0,
): LiabilityDraft {
  return {
    localId: Number(liability.id) || lshCreateLocalId() + index,
    backendId: liability.id ? Number(liability.id) : undefined,
    lender: String(liability.lender || ''),
    creditLimitAmount: lshFormatStoredValue(liability.creditLimitAmount),
    currentBalanceLimit: lshFormatStoredValue(liability.currentBalanceLimit),
    monthlyInstallmentAmount: lshFormatStoredValue(
      liability.monthlyInstallmentAmount,
    ),
    inArrears: Boolean(liability.inArrears),
    liabilityAccountTypeId: liability.liabilityAccountType?.id
      ? Number(liability.liabilityAccountType.id)
      : undefined,
    liabilityAccountTypeName: liability.liabilityAccountType?.name || undefined,
  };
}
